import {
  useCallback, useEffect, useRef, useState,
} from 'react';

import { cohortsApi } from '../api';
import type { CohortMember, CohortMemberProgress, PaginatedResponse } from '../types';

export interface UseCohortProgressResult {
  rows: CohortMemberProgress[];
  count: number;
  next: string | null;
  previous: string | null;
  loading: boolean;
  /** Last request failed; `errorStatus` is null for statusless failures too. */
  failed: boolean;
  errorStatus: number | null;
  refetch: () => void;
}

/** Identifies one (cohort, search, page) request, to detect a stale async response. */
const requestKeyOf = (
  slug: string | undefined,
  search: string,
  page: number,
  membersOnly: boolean,
): string => (
  `${slug ?? ''}::${search}::${page}::${membersOnly ? 'members' : 'progress'}`
);

/**
 * Normalizes the `/progress/` response into the paginated shape consumers
 * expect: the BE has a non-paginated fallback that returns a bare array
 * instead of `{count,next,previous,results}` — trusting `res.results`
 * unconditionally crashed `CohortMembers` render on that shape.
 */
const normalizeProgressResponse = (
  res: PaginatedResponse<CohortMemberProgress> | CohortMemberProgress[] | null | undefined,
): PaginatedResponse<CohortMemberProgress> => {
  if (Array.isArray(res)) {
    return {
      count: res.length, next: null, previous: null, results: res,
    };
  }
  return {
    count: res?.count ?? res?.results?.length ?? 0,
    next: res?.next ?? null,
    previous: res?.previous ?? null,
    results: res?.results ?? [],
  };
};

/**
 * Fetches one page of a cohort's member progress. Re-fetches whenever `slug`,
 * `search` or `page` changes; `refetch()` reloads the current page in place.
 * A response that lands after the caller has moved on is dropped.
 * `membersOnly` reads the plain member list instead (`/members/`, open to any
 * member, no search): rows come back with `progress: null`.
 */
export function useCohortProgress(
  slug: string | undefined,
  search: string,
  page: number,
  membersOnly: boolean = false,
): UseCohortProgressResult {
  const [rows, setRows] = useState<CohortMemberProgress[]>([]);
  const [count, setCount] = useState<number>(0);
  const [next, setNext] = useState<string | null>(null);
  const [previous, setPrevious] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [failed, setFailed] = useState<boolean>(false);
  const [errorStatus, setErrorStatus] = useState<number | null>(null);
  const [reloadIndex, setReloadIndex] = useState<number>(0);

  const activeRequestRef = useRef<string>('');

  useEffect(() => {
    if (!slug) {
      return;
    }

    const requestKey = requestKeyOf(slug, search, page, membersOnly);
    activeRequestRef.current = requestKey;
    setLoading(true);
    setFailed(false);
    setErrorStatus(null);

    const onSuccess = (
      res: PaginatedResponse<CohortMemberProgress> | CohortMemberProgress[],
    ): void => {
      if (activeRequestRef.current !== requestKey) {
        return; // stale response for a page/search the caller has since left
      }
      const normalized = normalizeProgressResponse(res);
      setRows(normalized.results);
      setCount(normalized.count);
      setNext(normalized.next);
      setPrevious(normalized.previous);
      setLoading(false);
    };

    const onError = (error: unknown): void => {
      if (activeRequestRef.current !== requestKey) {
        return;
      }
      setRows([]);
      setCount(0);
      setNext(null);
      setPrevious(null);
      setFailed(true);
      setErrorStatus((error as { status?: number })?.status ?? null);
      setLoading(false);
    };

    if (membersOnly) {
      cohortsApi.getCohortMembers(
        slug,
        { page },
        (res: PaginatedResponse<CohortMember>) => onSuccess({
          ...res,
          results: (res.results ?? []).map((member) => ({ ...member, progress: null })),
        }),
        onError,
      );
      return;
    }

    cohortsApi.getCohortProgress(slug, { page, search }, onSuccess, onError);
  }, [slug, search, page, membersOnly, reloadIndex]);

  const refetch = useCallback((): void => {
    setReloadIndex((index) => index + 1);
  }, []);

  return {
    rows, count, next, previous, loading, failed, errorStatus, refetch,
  };
}
